import { Col, Form } from 'react-bootstrap';
import Select from 'react-select';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import _ from 'lodash';
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';
import { ReactSelectOption } from 'src/types';
import { useApiClient } from 'src/hooks/useApiClient';
import SelectOptWithSubtitle from 'src/components/SelectOptWithSubtitle/SelectOptWithSubtitle';
import RequiredFormLabel from 'src/components/RequiredFormLabel/RequiredFormLabel';
import { modelOptionLabel, productionFetchParams } from './selectorMeta';
import type {
	MotornetMakeWithKind,
	MotornetModelSummary,
	MotornetVehicleKind,
	MotornetVersionSummary,
} from '../types';

export type MakeOption = ReactSelectOption & { data: MotornetMakeWithKind };
export type ModelOption = ReactSelectOption & { data: MotornetModelSummary };
export type VersionOption = ReactSelectOption & {
	subtitle: string;
	data: MotornetVersionSummary;
};

export type MotornetSelectedVehicle = {
	brand: MakeOption | ReactSelectOption | null;
	model: (ReactSelectOption & { data?: MotornetModelSummary }) | null;
	version: VersionOption | ReactSelectOption | null;
};

export const getMakeName = (
	brand: (ReactSelectOption & { data?: MotornetMakeWithKind }) | null | undefined
): string => brand?.data?.nome ?? brand?.label ?? '';

const MotornetVehicleSelector = forwardRef(
	(
		{
			selectedVehicle,
			onBrandChange,
			onModelChange,
			onVersionChange,
			vehicleKind,
			year,
			month,
			hydrateEurotax,
			showSelectLabels,
			requiredBrand,
			requiredModel,
			requiredVersion,
			hasBrandError,
			hasModelError,
			hasVersionError,
			disabled = false,
			vertical = false,
		}: {
			selectedVehicle: MotornetSelectedVehicle;
			onBrandChange: (opt: MakeOption | null) => void;
			onModelChange: (opt: ModelOption | null) => void;
			onVersionChange: (opt: VersionOption | null) => void;
			vehicleKind?: MotornetVehicleKind;
			year?: number;
			month?: number;
			hydrateEurotax?: string | null;
			showSelectLabels?: boolean;
			requiredBrand?: boolean;
			requiredModel?: boolean;
			requiredVersion?: boolean;
			hasBrandError?: boolean;
			hasModelError?: boolean;
			hasVersionError?: boolean;
			disabled?: boolean;
			vertical?: boolean;
		} & React.RefAttributes<any>,
		ref
	) => {
		const { apiClient } = useApiClient();
		const queryClient = useQueryClient();
		const { t } = useTranslation();

		const [makes, setMakes] = useState<MakeOption[]>([]);
		const [models, setModels] = useState<ModelOption[]>([]);
		const [versions, setVersions] = useState<VersionOption[]>([]);
		const [loadingMakes, setLoadingMakes] = useState(false);
		const [loadingModels, setLoadingModels] = useState(false);
		const [loadingVersions, setLoadingVersions] = useState(false);

		const makesReqId = useRef(0);
		const modelsReqId = useRef(0);
		const versionsReqId = useRef(0);
		const makesRef = useRef<MakeOption[]>([]);

		const brandOption = (selectedVehicle.brand as MakeOption | null) ?? null;
		const codiceMarca = brandOption?.value ?? null;
		const marcaKind = brandOption?.data?.vehicleKind;
		const codiceModello = selectedVehicle.model?.value ?? null;

		const loadMakes = useCallback(async () => {
			const reqId = ++makesReqId.current;
			setLoadingMakes(true);
			try {
				const data = await apiClient.motornetControllerService.getMakes();
				const opts: MakeOption[] = data
					.map((make) => ({
						label: _.startCase(_.toLower(make.nome)) + ` (${make.vehicleKind})`,
						value: make.acronimo,
						data: make,
					}))
					.sort((a, b) => a.label.localeCompare(b.label));
				if (reqId === makesReqId.current) {
					setMakes(opts);
					makesRef.current = opts;
				}
				return opts;
			} finally {
				if (reqId === makesReqId.current) setLoadingMakes(false);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		const resolveMakeKind = useCallback(
			(marca: string | null | undefined): MotornetVehicleKind | undefined =>
				(marca
					? makesRef.current.find((m) => m.value === marca)?.data.vehicleKind
					: undefined) ?? vehicleKind,
			[vehicleKind]
		);

		const loadModels = useCallback(
			async (marca: string, kind?: MotornetVehicleKind) => {
				const reqId = ++modelsReqId.current;
				setLoadingModels(true);
				try {
					const data = await apiClient.motornetControllerService.getModels(
						marca,
						{ vehicleKind: kind ?? resolveMakeKind(marca), year }
					);
					const opts: ModelOption[] = data
						.map((model) => ({
							label: modelOptionLabel(model),
							value: model.gammaModello?.codice ?? '',
							data: model,
						}))
						.sort((a, b) => a.label.localeCompare(b.label));
					if (reqId === modelsReqId.current) setModels(opts);
					return opts;
				} finally {
					if (reqId === modelsReqId.current) setLoadingModels(false);
				}
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[resolveMakeKind, year]
		);

		const loadVersions = useCallback(
			async (marca: string, modello: string, kind?: MotornetVehicleKind) => {
				const reqId = ++versionsReqId.current;
				setLoadingVersions(true);
				try {
					const data = await apiClient.motornetControllerService.getVersions({
						codiceMarca: marca,
						codiceModello: modello,
						vehicleKind: kind ?? resolveMakeKind(marca),
						year,
					});
					const opts: VersionOption[] = data.map((version) => ({
						label:
							version.nome || version.versione || version.codiceEurotax || '—',
						value: version.codiceEurotax ?? '',
						subtitle: version.codiceEurotax ?? '',
						data: version,
					}));
					if (reqId === versionsReqId.current) setVersions(opts);
					return opts;
				} finally {
					if (reqId === versionsReqId.current) setLoadingVersions(false);
				}
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[resolveMakeKind, year]
		);

		const hydrateFromEurotax = useCallback(
			async (eurotaxCode: string, kind?: MotornetVehicleKind) => {
				const code = eurotaxCode?.trim();
				if (!code) return null;

				const payload =
					await apiClient.motornetControllerService.getMotornetVehicle(
						code,
						kind,
						{ year, month }
					);
				const resolvedKind = payload?.vehicleKind ?? kind;
				queryClient.setQueryData(
					[
						'motornet',
						'vehicle',
						code,
						resolvedKind,
						year ?? null,
						month ?? null,
					],
					payload
				);
				const m = payload?.details?.modello;
				const marca = m?.marca?.acronimo;
				const modelCode = m?.gammaModello?.codice;
				if (!marca) return { brand: null, model: null, version: null };

				const makeOpts = await loadMakes();
				const brand =
					makeOpts.find(
						(o) => o.value === marca && o.data.vehicleKind === resolvedKind
					) ??
					makeOpts.find((o) => o.value === marca) ??
					null;
				onBrandChange(brand);
				if (!brand) return { brand: null, model: null, version: null };

				const brandKind = brand.data.vehicleKind;
				const modelOpts = await loadModels(marca, brandKind);
				const model = modelOpts.find((o) => o.value === modelCode) ?? null;
				onModelChange(model);
				if (!model) return { brand, model: null, version: null };

				const versionOpts = await loadVersions(marca, model.value, brandKind);
				const version = versionOpts.find((o) => o.value === code) ?? null;
				onVersionChange(version);

				return { brand, model, version };
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[vehicleKind, loadMakes, loadModels, loadVersions]
		);

		useEffect(() => {
			loadMakes();
		}, [loadMakes]);

		const hydratedCodeRef = useRef<string | null>(null);
		useEffect(() => {
			const code = hydrateEurotax?.trim();
			if (!code || hydratedCodeRef.current === code) return;
			hydratedCodeRef.current = code;
			hydrateFromEurotax(code);
		}, [hydrateEurotax, hydrateFromEurotax]);

		useEffect(() => {
			setModels([]);
			setVersions([]);
			if (codiceMarca) {
				loadModels(codiceMarca, marcaKind);
			}
		}, [codiceMarca, marcaKind, loadModels]);

		useEffect(() => {
			setVersions([]);
			if (codiceMarca && codiceModello) {
				loadVersions(codiceMarca, codiceModello, marcaKind);
			}
		}, [codiceMarca, codiceModello, marcaKind, loadVersions]);

		useImperativeHandle(
			ref,
			() => ({ loadMakes, loadModels, loadVersions, hydrateFromEurotax }),
			[loadMakes, loadModels, loadVersions, hydrateFromEurotax]
		);

		const cols = vertical ? 12 : 4;

		const handleBrandChange = (opt: MakeOption | null) => {
			onBrandChange(opt);
			onModelChange(null);
			onVersionChange(null);
		};

		const handleModelChange = (opt: ModelOption | null) => {
			onModelChange(opt);
			onVersionChange(null);
		};

		const handleVersionChange = (opt: VersionOption | null) => {
			onVersionChange(opt);
			const code = opt?.value?.trim();
			if (!code) return;
			const makeKind =
				opt?.data?.vehicleKind ?? marcaKind ?? resolveMakeKind(codiceMarca);
			// Su ES il veicolo si chiede all'inizio produzione.
			const params = productionFetchParams(
				opt?.data,
				selectedVehicle.model?.data
			);
			const fetchYear = year ?? params.year;
			const fetchMonth = month ?? params.month;
			queryClient.prefetchQuery({
				queryKey: [
					'motornet',
					'vehicle',
					code,
					makeKind,
					fetchYear ?? null,
					fetchMonth ?? null,
				],
				queryFn: () =>
					apiClient.motornetControllerService.getMotornetVehicle(
						code,
						makeKind,
						{ year: fetchYear, month: fetchMonth }
					),
				staleTime: 5 * 60 * 1000,
			});
		};

		return (
			<>
				<Col md={cols}>
					<Form.Group>
						{showSelectLabels && (
							<RequiredFormLabel
								label={i18next.t('brand')}
								required={requiredBrand}
							/>
						)}
						<Select
							menuPosition="fixed"
							isClearable={!disabled}
							isLoading={loadingMakes}
							isDisabled={!makes.length || disabled}
							placeholder={t('select_brand')}
							name="motornet_brand"
							value={selectedVehicle.brand}
							options={makes}
							getOptionValue={(o: any) =>
								o?.data
									? `${o.data.acronimo}:${o.data.vehicleKind}`
									: o?.value ?? ''
							}
							onChange={(opt) => handleBrandChange(opt as MakeOption | null)}
							className={hasBrandError ? 'select-error' : ''}
						/>
					</Form.Group>
				</Col>

				<Col md={cols}>
					<Form.Group>
						{showSelectLabels && (
							<RequiredFormLabel
								label={i18next.t('model')}
								required={requiredModel}
							/>
						)}
						<Select
							menuPosition="fixed"
							isClearable={!disabled}
							isLoading={loadingModels}
							isDisabled={!models.length || disabled}
							placeholder={t('select_model')}
							name="motornet_model"
							value={selectedVehicle.model}
							options={models}
							onChange={(opt) => handleModelChange(opt as ModelOption | null)}
							className={hasModelError ? 'select-error' : ''}
						/>
					</Form.Group>
				</Col>

				<Col md={cols}>
					<Form.Group>
						{showSelectLabels && (
							<RequiredFormLabel
								label={i18next.t('allestimento')}
								required={requiredVersion}
							/>
						)}
						<Select
							menuPosition="fixed"
							isClearable={!disabled}
							isLoading={loadingVersions}
							isDisabled={!versions.length || disabled}
							placeholder={t('select_version', 'Seleziona allestimento')}
							name="motornet_version"
							value={selectedVehicle.version}
							options={versions}
							onChange={(opt) =>
								handleVersionChange(opt as VersionOption | null)
							}
							className={hasVersionError ? 'select-error' : ''}
							components={{
								Option: (props: any) => <SelectOptWithSubtitle {...props} />,
							}}
						/>
					</Form.Group>
				</Col>
			</>
		);
	}
);

export default MotornetVehicleSelector;
